import React, { useMemo, useEffect } from 'react';
import { Box, IconButton, Typography, Button, Card, CardContent, Divider, Stack } from '@mui/material';
import { useNotificationTrigger } from '@app/shared/hooks/useNotificationTrigger';
import { useRecoilValue, useSetRecoilState, useRecoilState } from 'recoil';
import { atom_tourState, atom_originalTourState } from '@app/shared/state/tour.atom';
import { atom_openedStopState } from '@app/shared/state/opened-stop.atom';
import { atom_activeStopState } from '@app/shared/state/active-stop.atom';
import { atom_bookmarkedStopsState, atom_allBookmarksState } from '@app/shared/state/filter.atom';
import { atom_listModeState } from '@app/shared/state/list-mode.atom';
import { atom_navigationHelperState } from '@app/shared/state/show-content-modal.atom';
import { useTranslation } from 'react-i18next';
import { Stop } from '@app/shared/models/tour.type';
import { service_tourService } from '@app/shared/services/tours.service';
import giftGIF from '@app/assets/giftGIF.gif';

// Map icon names to FontAwesome classes
const CTA_ICON_MAP: Record<string, string> = {
	gift: 'fa-solid fa-gift',
	info: 'fa-solid fa-circle-info',
	star: 'fa-solid fa-star',
	heart: 'fa-solid fa-heart',
	bell: 'fa-solid fa-bell',
	map: 'fa-solid fa-map-marker-alt',
	compass: 'fa-solid fa-compass',
	camera: 'fa-solid fa-camera',
	ticket: 'fa-solid fa-ticket',
	coffee: 'fa-solid fa-mug-hot',
	food: 'fa-solid fa-utensils',
	warning: 'fa-solid fa-triangle-exclamation',
	check: 'fa-solid fa-circle-check',
	discount: 'fa-solid fa-percent',
	time: 'fa-solid fa-clock',
	walk: 'fa-solid fa-person-walking',
};

function isHebrew() {
	const lang = (typeof window !== 'undefined' && window.localStorage.getItem('i18nextLng')) || 'en';
	return lang === 'he';
}

interface CTAStopCardProps {
	stop: Stop;
	tourColor: string;
	tourId: string;
	onCardClick: () => void;
}

const CTAStopCard = React.memo(function CTAStopCard({
	stop,
	tourColor,
	tourId,
	onCardClick,
}: CTAStopCardProps) {
	const { t } = useTranslation();
	const bookmarkedStops = useRecoilValue(atom_bookmarkedStopsState);
	const [allBookmarks, setAllBookmarks] = useRecoilState(atom_allBookmarksState);

	const isBookmarked = bookmarkedStops.includes(stop.id);
	const stopImage = stop.loc_pics && stop.loc_pics.length > 0 ? stop.loc_pics[0].url : null;

	const handleBookmarkClick = async (e: React.MouseEvent) => {
		e.stopPropagation();
		if (!tourId) return;

		const currentTourBookmarks = [...bookmarkedStops];
		const isAdding = !currentTourBookmarks.includes(stop.id);

		if (isAdding) {
			currentTourBookmarks.push(stop.id);
		} else {
			const index = currentTourBookmarks.indexOf(stop.id);
			if (index > -1) {
				currentTourBookmarks.splice(index, 1);
			}
		}

		setAllBookmarks({
			...allBookmarks,
			[tourId]: currentTourBookmarks,
		});

		const { uID } = JSON.parse(localStorage.getItem('credential_items') || '{}');
		if (uID) {
			if (isAdding) {
				await service_tourService.saveSavedStop(uID, tourId, stop.id);
			} else {
				await service_tourService.deleteSavedStop(uID, tourId, stop.id);
			}
		}
	};

	return (
		<Card
			elevation={2}
			onClick={onCardClick}
			sx={{
				display: 'flex',
				padding: '12px',
				flexDirection: 'column',
				alignItems: 'flex-start',
				gap: '8px',
				alignSelf: 'stretch',
				borderRadius: '8px',
				background: '#FFF',
				boxShadow: '0px 2px 8px 0px rgba(0, 0, 0, 0.12)',
				cursor: 'pointer',
				transition: 'box-shadow 0.2s',
				border: `1px solid ${tourColor}`,
				'&:hover': {
					boxShadow: '0px 4px 12px 0px rgba(0, 0, 0, 0.18)',
				},
			}}
		>
			<CardContent
				sx={{
					pt: '4px',
					pb: '4px',
					px: '4px',
					width: '100%',
					height: 'auto',
					flex: 1,
				}}
			>
				<Box sx={{ display: 'flex', width: '100%', gap: 2 }}>
					<Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
						<Typography
							variant="subtitle1"
							component="h3"
							sx={{
								fontWeight: 'bold',
								color: 'rgba(31,49,59,1)',
								fontSize: '14px',
							}}
						>
							{stop.s_title}
						</Typography>
						{stop.s_smallDesc && (
							<Typography
								variant="body2"
								color="text.primary"
								sx={{
									mt: 0.5,
									fontSize: '13px',
									display: '-webkit-box',
									overflow: 'hidden',
									wordBreak: 'break-word',
									WebkitBoxOrient: 'vertical',
									WebkitLineClamp: 2,
								}}
							>
								{stop.s_smallDesc}
							</Typography>
						)}
						{stop.tags && stop.tags.length > 0 && (
							<Stack
								direction="row"
								alignItems="center"
								sx={{ mt: 'auto', flexWrap: 'wrap', gap: '4px', paddingTop: '5px' }}
							>
								{stop.tags.slice(0, 2).map((tag, index) => (
									<React.Fragment key={tag.text + index}>
										<Typography
											variant="body2"
											component="span"
											sx={{
												fontWeight: 700,
												fontSize: '11px',
												lineHeight: '16px',
											}}
										>
											{tag.text}
										</Typography>
										{index < Math.min(stop.tags.length, 2) - 1 && (
											<Box
												sx={{
													bgcolor: '#48526B',
													width: '3px',
													height: '3px',
													borderRadius: '50%',
													ml: '5px',
													mr: '5px',
												}}
											/>
										)}
									</React.Fragment>
								))}
							</Stack>
						)}
					</Box>
					{stopImage && (
						<img
							src={stopImage}
							alt={stop.s_title}
							style={{
								width: 70,
								height: 70,
								objectFit: 'cover',
								borderRadius: 4,
							}}
						/>
					)}
				</Box>
			</CardContent>

			<Divider
				variant="fullWidth"
				sx={{ width: '100%', borderColor: '#D5D9E2', borderBottomWidth: '1px' }}
			/>

			<Box sx={{ px: 0.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', py: 0.5 }}>
				<Button
					variant="text"
					sx={{
						color: '#000',
						fontWeight: 600,
						backgroundColor: 'transparent',
						fontSize: 13,
						px: 0,
						minWidth: 'auto',
						gap: '6px',
						textTransform: 'none',
					}}
				>
					{t('new_app.explore_more')}
					<i
						className={isHebrew() ? 'fa-solid fa-angle-left' : 'fa-solid fa-angle-right'}
						aria-hidden="true"
						style={{ color: '#000', fontSize: 20, fontWeight: 300 }}
					/>
				</Button>
				<IconButton
					onClick={handleBookmarkClick}
					size="small"
					aria-label={(isBookmarked ? t('accessibility.remove_from_saved') : t('accessibility.add_to_saved')) as string}
				>
					<i
						className={isBookmarked ? 'fa-solid fa-heart' : 'fa-regular fa-heart'}
						style={{ color: isBookmarked ? tourColor : 'black', fontSize: 18 }}
					/>
				</IconButton>
			</Box>
		</Card>
	);
});

export const DesktopCTAContent: React.FC = () => {
	const { t, i18n } = useTranslation();
	const { activeCTA, dismissCTA, markAsSeen } = useNotificationTrigger();
	const tourState = useRecoilValue(atom_tourState);
	const originalTour = useRecoilValue(atom_originalTourState);
	const setOpenedStop = useSetRecoilState(atom_openedStopState);
	const setActiveStop = useSetRecoilState(atom_activeStopState);
	const setListMode = useSetRecoilState(atom_listModeState);
	const setNavState = useSetRecoilState(atom_navigationHelperState);

	// Get tour color or fallback
	const tourColor = tourState?.config?.mainColor || '#E65100';

	// nav-start CTAs render OpeningStopBlock-style (centered image + Take me there / Skip).
	const isNavStartCTA = activeCTA?.id?.startsWith('nav-start-') ?? false;

	// Find stop if actionType is navigate, modal, or openStop (actionValue = stop-id).
	// nav-start uses tourState (originalTour is dead in most flows); regular CTAs keep originalTour.
	const linkedStop = useMemo(() => {
		const actionType = activeCTA?.ctaConfig?.actionType;
		const actionValue = activeCTA?.ctaConfig?.actionValue;

		if (!actionValue) return null;
		if (actionType !== 'navigate' && actionType !== 'modal' && actionType !== 'openStop') return null;

		const stops = isNavStartCTA ? tourState?.stops : originalTour?.stops;
		if (!stops) return null;

		return stops.find(stop => stop.id === actionValue);
	}, [isNavStartCTA, activeCTA?.ctaConfig?.actionType, activeCTA?.ctaConfig?.actionValue, tourState?.stops, originalTour?.stops]);

	// Set active stop when linkedStop is found
	useEffect(() => {
		if (linkedStop) {
			setActiveStop(linkedStop);
		}
	}, [linkedStop, setActiveStop]);

	// Only show if there's an active CTA
	if (!activeCTA) {
		return null;
	}

	const { ctaConfig } = activeCTA;

	// Handle CTA button click based on action type
	// Primary button only performs action, does NOT dismiss or mark as seen
	const handleCTAClick = () => {
		if (!ctaConfig) return;

		switch (ctaConfig.actionType) {
			case 'link':
				if (ctaConfig.actionValue) {
					window.open(ctaConfig.actionValue, '_blank');
				}
				break;
			case 'navigate':
				if (linkedStop) {
					setListMode('desktop');
				}
				break;
			case 'modal':
			case 'openStop':
				if (linkedStop) {
					setOpenedStop(linkedStop);
				}
				break;
			default:
				break;
		}
		// Track that user clicked the CTA (but don't dismiss)
		markAsSeen(activeCTA.id, 'ctaClicked');
	};

	// Handle stop card click - same as primary button, only performs action
	const handleStopCardClick = () => {
		if (linkedStop) {
			setOpenedStop(linkedStop);
			markAsSeen(activeCTA.id, 'ctaClicked');
		}
	};

	// nav-start "Take me there" on desktop: open NavigationHelperContent as a popover anchored
	// to the clicked button — matches MapCard.tsx:527 / MapOverlays.tsx:502 desktop pattern.
	// We deliberately keep the host modal open (don't call dismissCTA / setShowModal here):
	// the MUI Popover positions itself via anchorEl.getBoundingClientRect(), and unmounting
	// the button in the same tick would put the popover at (0,0). User dismisses the CTA via Skip.
	const handleNavStartPrimary = (e: React.MouseEvent<HTMLElement>) => {
		if (!linkedStop) return;
		setActiveStop(linkedStop);
		setNavState({
			anchorEl: e.currentTarget,
			offset: { top: 0, x: -15 },
			usePopover: true,
		});
		markAsSeen(activeCTA.id, 'ctaClicked');
	};

	return (
		<Box
			role="document"
			sx={{
				backgroundColor: 'white',
				direction: isHebrew() ? 'rtl' : 'ltr',
				textAlign: 'center',
				padding: '24px',
				paddingTop: '50px',
				minWidth: '300px',
			}}
		>
			{/* Icon + Title + Content wrapper with GIF background */}
			<Box sx={{ position: 'relative', clipPath: 'inset(-100% -100% 0 -100%)' }}>
				{/* Gift GIF background - plays once on CTA display */}
				{activeCTA.icon === 'gift' && (
					<Box
						sx={{
							position: 'absolute',
							top: '50%',
							left: '50%',
							transform: 'translate(-50%, -50%)',
							zIndex: 0,
							pointerEvents: 'none',
						}}
					>
						<img
							key={`gift-gif-${activeCTA.id}`}
							src={giftGIF}
							alt=""
							aria-hidden="true"
							style={{
								width: 380,
								height: 260,
								objectFit: 'contain',
							}}
						/>
					</Box>
				)}

				{/* Icon */}
				{activeCTA.icon && CTA_ICON_MAP[activeCTA.icon] && (
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'center',
							marginBottom: 2.5,
							position: 'relative',
							zIndex: 1,
						}}
					>
						<Box
							sx={{
								padding: '22px',
								borderRadius: '50%',
								backgroundColor: `${tourColor}20`,
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
							}}
						>
							<i
								className={CTA_ICON_MAP[activeCTA.icon]}
								style={{
									fontSize: '30px',
									color: tourColor,
								}}
							/>
						</Box>
					</Box>
				)}

				{/* Title */}
				<Typography
					variant="h5"
					component="h2"
					sx={{
						fontWeight: 600,
						fontSize: '18px',
						lineHeight: '26px',
						textAlign: 'center',
						color: '#333',
						marginBottom: '10px',
						paddingX: 2,
						wordBreak: 'keep-all',
						overflowWrap: 'break-word',
						position: 'relative',
						zIndex: 1,
					}}
				>
					{activeCTA.title}
				</Typography>

				{/* Content */}
				<Typography
					component="div"
					variant="body1"
					sx={{
						fontWeight: 400,
						fontSize: '15px',
						lineHeight: '24px',
						textAlign: 'center',
						color: '#666',
						marginBottom: '20px',
						paddingX: 1,
						wordBreak: 'keep-all',
						overflowWrap: 'break-word',
						'& p': { margin: 0 },
						'& strong': { fontWeight: 600 },
						position: 'relative',
						zIndex: 1,
					}}
					dangerouslySetInnerHTML={{ __html: activeCTA.content }}
				/>
			</Box>

			{/* nav-start (OpeningStopBlock-style): centered image, no stop card */}
			{isNavStartCTA && activeCTA.thumbnail && (
				<Box sx={{ display: 'flex', justifyContent: 'center', mt: 1, mb: 2 }}>
					<img
						src={activeCTA.thumbnail}
						alt=""
						style={{
							maxHeight: '35vh',
							maxWidth: '100%',
							borderRadius: 12,
							objectFit: 'cover',
						}}
					/>
				</Box>
			)}

			{/* Stop Card (regular CTAs only) */}
			{!isNavStartCTA && linkedStop && tourState?.id && (
				<Box sx={{ width: '100%', mt: 1, mb: 2, textAlign: 'initial' }}>
					<CTAStopCard
						stop={linkedStop}
						tourColor={tourColor}
						tourId={tourState.id}
						onCardClick={handleStopCardClick}
					/>
				</Box>
			)}

			{/* Action Buttons */}
			<Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', width: '100%', mt: 2 }}>
				{/* Primary Button — nav-start uses the "Take me there" flow (open NavigationHelperContent popover) */}
				{ctaConfig?.primaryButtonText && (
					<Button
						variant="contained"
						onClick={isNavStartCTA ? handleNavStartPrimary : handleCTAClick}
						sx={{
							height: '44px',
							backgroundColor: tourColor,
							color: '#fff',
							borderRadius: '22px',
							padding: '10px 24px',
							fontWeight: 600,
							textTransform: 'none',
							fontSize: '15px',
							boxShadow: 'none',
							'&:hover': {
								backgroundColor: tourColor,
								opacity: 0.9,
								boxShadow: 'none',
							},
						}}
					>
						{ctaConfig.primaryButtonText}
					</Button>
				)}

				{/* Secondary Button - Skip/Dismiss. dismissCTA() now clears both modal atoms
				    on desktop when the queue is empty, matching how every other modal closes. */}
				{ctaConfig?.secondaryButtonText && (
					<Button
						variant="text"
						onClick={() => {
							markAsSeen(activeCTA.id, 'seen');
							dismissCTA();
						}}
						sx={{
							height: '40px',
							color: '#666',
							borderRadius: '20px',
							padding: '10px 24px',
							fontWeight: 500,
							textTransform: 'none',
							fontSize: '14px',
							'&:hover': {
								backgroundColor: 'rgba(0,0,0,0.04)',
							},
						}}
					>
						{ctaConfig.secondaryButtonText}
					</Button>
				)}
			</Box>
		</Box>
	);
};

export default DesktopCTAContent;
